/**
 * Brand logo carousel – infinite loop, prev/next, autoplay, speed control.
 * brand-carousel-section.js
 */
class BrandCarouselSection extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.cardGap = 20;
    this.cardsPerView = 4;
    this.cardsPerViewMobile = 2;
    this.originalCount = 0;
    this.infinite = true;
    this.autoplay = false;
    this.autoplaySpeed = 4000;
    this._resizeHandler = null;
    this._autoplayTimer = null;
    this._isTransitioning = false;
  }

  connectedCallback() {
    this.track = this.querySelector('.brand-carousel-track');
    this.prevBtn = this.querySelector('.brand-carousel-prev');
    this.nextBtn = this.querySelector('.brand-carousel-next');

    if (!this.track || !this.prevBtn || !this.nextBtn) return;

    const cardsPerView = this.getAttribute('data-cards-per-view');
    const cardsPerViewMobile = this.getAttribute('data-cards-per-view-mobile');
    const gap = this.getAttribute('data-card-gap');
    const autoplayAttr = this.getAttribute('data-autoplay');
    const autoplaySpeedAttr = this.getAttribute('data-autoplay-speed');

    if (cardsPerView) this.cardsPerView = parseInt(cardsPerView, 10);
    if (cardsPerViewMobile) this.cardsPerViewMobile = parseInt(cardsPerViewMobile, 10);
    if (gap) this.cardGap = parseInt(gap, 10);
    if (autoplayAttr === 'true') this.autoplay = true;
    if (autoplaySpeedAttr) this.autoplaySpeed = parseInt(autoplaySpeedAttr, 10) * 1000;

    this.cards = Array.from(this.track.querySelectorAll('.brand-carousel-card'));
    if (this.cards.length === 0) return;

    this.originalCount = this.cards.length;
    // Clone cards for infinite loop
    this.cards.forEach((card) => {
      this.track.appendChild(card.cloneNode(true));
    });
    this.cards = Array.from(this.track.querySelectorAll('.brand-carousel-card'));

    this.dotsContainer = this.querySelector('.brand-carousel-dots');
    if (this.dotsContainer && this.originalCount > 0) {
      this.dots = [];
      for (let i = 0; i < this.originalCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'brand-carousel-dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        dot.addEventListener('click', () => this.goToSlide(i));
        this.dotsContainer.appendChild(dot);
        this.dots.push(dot);
      }
    } else {
      this.dots = [];
    }

    this.prevBtn.addEventListener('click', () => this.prev());
    this.nextBtn.addEventListener('click', () => this.next());

    this._resizeHandler = () => this.update();
    window.addEventListener('resize', this._resizeHandler);

    this.track.addEventListener('transitionend', () => this._onTransitionEnd());

    this.wrapper = this.querySelector('.brand-carousel-wrapper');
    if (this.wrapper) {
      this.wrapper.addEventListener('mouseenter', () => this._pauseAutoplay());
      this.wrapper.addEventListener('mouseleave', () => this._startAutoplay());
    }

    this.update();
    this._startAutoplay();
  }

  disconnectedCallback() {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
    this._clearAutoplay();
  }

  _startAutoplay() {
    if (!this.autoplay || this._autoplayTimer) return;
    this._autoplayTimer = setInterval(() => this.next(), this.autoplaySpeed);
  }

  _pauseAutoplay() {
    this._clearAutoplay();
  }

  _clearAutoplay() {
    if (this._autoplayTimer) {
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = null;
    }
  }

  _onTransitionEnd() {
    this._isTransitioning = false;
    if (this.infinite && this.originalCount > 0) {
      if (this.currentIndex >= this.originalCount) {
        this._applyTransition(false);
        this.currentIndex = 0;
        this._applyTransform();
        this._applyTransition(true);
      } else if (this.currentIndex < 0) {
        this._applyTransition(false);
        this.currentIndex = this.originalCount - 1;
        this._applyTransform();
        this._applyTransition(true);
      }
    }
    if (this.prevBtn) this.prevBtn.disabled = false;
    if (this.nextBtn) this.nextBtn.disabled = false;
  }

  _applyTransition(enable) {
    if (!this.track) return;
    this.track.style.transition = enable ? '' : 'none';
  }

  _applyTransform() {
    if (!this.track) return;
    const offset = this.currentIndex * this.getStepOffset();
    this.track.style.transform = `translateX(-${offset}px)`;
  }

  getCardsPerView() {
    return window.matchMedia('(max-width: 767px)').matches
      ? this.cardsPerViewMobile
      : this.cardsPerView;
  }

  getMaxIndex() {
    if (this.infinite) return this.originalCount - 1;
    const cardsPerView = this.getCardsPerView();
    return Math.max(0, this.originalCount - cardsPerView);
  }

  getStepOffset() {
    if (this.cards.length === 0) return 0;
    const first = this.cards[0];
    const rect = first.getBoundingClientRect();
    return rect.width + this.cardGap;
  }

  update() {
    const maxIndex = this.getMaxIndex();
    if (!this.infinite) {
      this.currentIndex = Math.min(Math.max(0, this.currentIndex), maxIndex);
    }

    this._applyTransform();
    this._updateDots();

    if (this.infinite) {
      if (this.prevBtn) this.prevBtn.disabled = false;
      if (this.nextBtn) this.nextBtn.disabled = false;
    } else {
      if (this.prevBtn) this.prevBtn.disabled = this.currentIndex <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentIndex >= maxIndex;
    }
  }

  _updateDots() {
    if (!this.dots || this.dots.length === 0) return;
    const activeIndex = ((this.currentIndex % this.originalCount) + this.originalCount) % this.originalCount;
    this.dots.forEach((dot, i) => {
      const isActive = i === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  goToSlide(index) {
    if (this._isTransitioning || index < 0 || index >= this.originalCount) return;
    this._isTransitioning = true;
    this.currentIndex = index;
    this.update();
    this._isTransitioning = false;
  }

  prev() {
    if (this._isTransitioning) return;
    if (this.infinite) {
      this.currentIndex -= 1;
      if (this.currentIndex < 0) {
        this._isTransitioning = true;
        this._applyTransition(false);
        this.currentIndex = this.originalCount - 1;
        this._applyTransform();
        this._updateDots();
        requestAnimationFrame(() => {
          this._applyTransition(true);
          this._isTransitioning = false;
        });
        return;
      }
      this._isTransitioning = true;
      this.update();
    } else {
      if (this.currentIndex <= 0) return;
      this.currentIndex -= 1;
      this.update();
    }
  }

  next() {
    if (this._isTransitioning) return;
    if (this.infinite) {
      this._isTransitioning = true;
      this.currentIndex += 1;
      if (this.currentIndex >= this.originalCount) {
        this._applyTransform();
        return;
      }
      this.update();
    } else {
      const maxIndex = this.getMaxIndex();
      if (this.currentIndex >= maxIndex) return;
      this.currentIndex += 1;
      this.update();
    }
  }
}

if (!customElements.get('brand-carousel-section')) {
  customElements.define('brand-carousel-section', BrandCarouselSection);
}
